import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useDayNightMode } from '@/hooks/use-day-night-mode';
import type { ThemeMode } from '@/lib/theme';

type ThemeContextValue = {
  mode: ThemeMode;
  toggle: () => void;
  resetOverride: () => void;
  hasOverride: boolean;
};

const ThemeContext = createContext<ThemeContextValue>({
  mode: 'night',
  toggle: () => {},
  resetOverride: () => {},
  hasOverride: false,
});

export function ThemeProvider({
  children,
  forceMode,
  surface,
}: {
  children: ReactNode;
  forceMode?: ThemeMode;
  /* "ops" keeps the Ops Center on its own dark palette (index.css,
     [data-surface="ops"]). Every other surface takes the web's palette. */
  surface?: 'ops';
}) {
  const dayNight = useDayNightMode();
  const mode = forceMode ?? dayNight.mode;

  useEffect(() => {
    const html = document.documentElement;
    html.setAttribute('data-mode', mode);
    html.classList.remove('day', 'night');
    html.classList.add(mode);
  }, [mode]);

  useEffect(() => {
    const html = document.documentElement;
    if (surface) html.setAttribute('data-surface', surface);
    else html.removeAttribute('data-surface');
    return () => { html.removeAttribute('data-surface'); };
  }, [surface]);

  return (
    <ThemeContext.Provider value={{ ...dayNight, mode }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
